/**
 * Отчёты (раздел 6.18): готовые отчёты (АН-07) — запуск и выпуски с номерами ОТЧ-NNNN, мои отчёты
 * конструктора (АН-05), ссылка на настраиваемые списки и подписка на сводку руководителю в 08:00.
 */
import {
  type CustomReport,
  type DigestPref,
  READY_REPORTS,
  type ReadyReportKind,
  type ReportRun,
  type ReportRunFull,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { FileText, ListChecks, Play } from 'lucide-react';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime, todayLocal } from '../../lib/format';
import { counterpartiesQuery, projectsQuery } from '../../lib/projects';
import { useCan } from '../../lib/session';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { Tabs } from '../psd/shared';

type Tab = 'ready' | 'mine' | 'digest';
const KINDS = Object.keys(READY_REPORTS) as ReadyReportKind[];
const needsProject = (k: ReadyReportKind) => k === 'weekly_customer' || k === 'plan_fact';
const ctl = 'h-11 md:h-10';
const HOURS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

function LaunchForm({ onDone }: { onDone: (r: ReportRunFull) => void }) {
  const [kind, setKind] = useState<ReadyReportKind>('weekly_customer');
  const [projectId, setProjectId] = useState('');
  const [counterpartyId, setCounterpartyId] = useState('');
  const [weekOf, setWeekOf] = useState('');
  const [asOf, setAsOf] = useState('');
  const projects = useQuery(projectsQuery('active'));
  const partners = useQuery({ ...counterpartiesQuery(), enabled: kind === 'contractor' });
  const build = useMutation({
    mutationFn: () =>
      api<ReportRunFull>('/reports/ready', {
        method: 'POST',
        body: {
          kind,
          projectId: needsProject(kind) ? projectId || null : null,
          counterpartyId: kind === 'contractor' ? counterpartyId || null : null,
          weekOf: kind === 'weekly_customer' ? weekOf || null : null,
          asOf: asOf || null,
        },
      }),
    onSuccess: onDone,
  });
  const ready = (!needsProject(kind) || !!projectId) && (kind !== 'contractor' || !!counterpartyId);
  return (
    <Panel title="Выпустить отчёт" meta="Данные фиксируются на дату среза и не меняются задним числом">
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Какой отчёт">
          <Select className={ctl} value={kind} onChange={(e) => setKind(e.target.value as ReadyReportKind)}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {READY_REPORTS[k]}
              </option>
            ))}
          </Select>
        </Field>
        {needsProject(kind) ? (
          <Field label="Проект">
            <Select className={ctl} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">— выберите —</option>
              {(projects.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} · {p.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        {kind === 'contractor' ? (
          <Field label="Подрядчик">
            <Select
              className={ctl}
              value={counterpartyId}
              onChange={(e) => setCounterpartyId(e.target.value)}
            >
              <option value="">— выберите —</option>
              {(partners.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        {kind === 'weekly_customer' ? (
          <Field label="Неделя" hint="Любая дата внутри недели; если пусто — текущая неделя">
            <Input className={ctl} type="date" value={weekOf} onChange={(e) => setWeekOf(e.target.value)} />
          </Field>
        ) : null}
        <Field label="Дата среза" hint="На какое число собраны данные; если пусто — на сегодня">
          <Input
            className={ctl}
            type="date"
            max={todayLocal()}
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
        </Field>
      </div>
      <div className="mt-4">
        <Button
          variant="primary"
          icon={<Play className="size-4" />}
          disabled={!ready}
          loading={build.isPending}
          onClick={() => build.mutate()}
        >
          Выпустить
        </Button>
      </div>
      {build.error ? <ErrorBox error={build.error} className="mt-3" /> : null}
    </Panel>
  );
}

function RunsList() {
  const [kind, setKind] = useState('');
  const [projectId, setProjectId] = useState('');
  const projects = useQuery(projectsQuery('active'));
  const runs = useQuery({
    queryKey: ['reports', 'runs', kind, projectId],
    queryFn: () =>
      api<ReportRun[]>('/reports/ready/runs', {
        query: { kind: kind || undefined, projectId: projectId || undefined },
      }),
  });
  return (
    <Panel title="Выпуски" bodyClassName="p-0">
      <div className="flex flex-col gap-2 border-b border-line px-4 py-3 md:flex-row">
        <Select
          className={`${ctl} md:w-64`}
          aria-label="Вид отчёта"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
        >
          <option value="">Все виды</option>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {READY_REPORTS[k]}
            </option>
          ))}
        </Select>
        <Select
          className={`${ctl} md:w-72`}
          aria-label="Проект"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
        >
          <option value="">Все проекты</option>
          {(projects.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.code} · {p.name}
            </option>
          ))}
        </Select>
      </div>
      {runs.isPending ? (
        <Spinner />
      ) : runs.isError ? (
        <ErrorBox error={runs.error} className="m-4" />
      ) : runs.data.length ? (
        <ul className="divide-y divide-line">
          {runs.data.map((r) => (
            <li key={r.id}>
              <Link
                to="/reports/runs/$id"
                params={{ id: r.id }}
                className="flex min-h-11 flex-col gap-1 px-4 py-3 hover:bg-tile/60"
              >
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="num text-[12px] text-ink-2">{r.number}</span>
                  <span className="font-medium [overflow-wrap:anywhere]">{r.title}</span>
                  {r.origin === 'schedule' ? <Badge tone="info">по расписанию</Badge> : null}
                  {!r.moneyShown ? <Badge tone="neutral">без сумм</Badge> : null}
                </span>
                <span className="text-[13px] text-ink-2">
                  {r.kindTitle}
                  {r.projectCode ? ` · ${r.projectCode}` : ''}
                  {r.periodFrom && r.periodTo ? ` · ${date(r.periodFrom)} – ${date(r.periodTo)}` : ''} · на{' '}
                  {date(r.asOf)} · выпущен {dateTime(r.createdAt)}
                  {r.createdByName ? `, ${r.createdByName}` : ''}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title="Выпусков пока нет">Выпустите первый отчёт выше.</EmptyState>
      )}
    </Panel>
  );
}

function MyReports() {
  const mayEdit = useCan('analytics.report.edit');
  const q = useQuery({
    queryKey: ['reports', 'custom'],
    queryFn: () => api<CustomReport[]>('/reports/custom'),
  });
  return (
    <div className="flex flex-col gap-6">
      <Panel
        title="Отчёты конструктора"
        actions={
          mayEdit ? (
            <Link
              to="/reports/builder"
              className="inline-flex h-11 items-center rounded-md bg-accent px-4 text-[14px] font-semibold text-on-accent hover:underline md:h-10"
            >
              Новый отчёт
            </Link>
          ) : undefined
        }
        bodyClassName="p-0"
      >
        {q.isPending ? (
          <Spinner />
        ) : q.isError ? (
          <ErrorBox error={q.error} className="m-4" />
        ) : q.data.length ? (
          <ul className="divide-y divide-line">
            {q.data.map((r) => (
              <li key={r.id}>
                <Link
                  to="/reports/custom/$id"
                  params={{ id: r.id }}
                  className="flex min-h-11 flex-col gap-0.5 px-4 py-3 hover:bg-tile/60"
                >
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium [overflow-wrap:anywhere]">{r.name}</span>
                    <Badge tone={r.shared ? 'info' : 'neutral'}>{r.shared ? 'общий' : 'личный'}</Badge>
                  </span>
                  <span className="text-[13px] text-ink-2">
                    {r.mine ? 'Ваш' : `Автор: ${r.ownerName ?? '—'}`} · {r.description ?? r.source}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title="Своих отчётов пока нет">
            Соберите отчёт в конструкторе: источник, поля, отбор, группировки, итоги.
          </EmptyState>
        )}
      </Panel>
      <Link
        to="/reports/lists"
        className="inline-flex min-h-11 items-center gap-2 text-[14px] text-accent-text underline-offset-4 hover:underline"
      >
        <ListChecks className="size-4" /> Настраиваемые списки и выгрузка в Excel и PDF
      </Link>
    </div>
  );
}

function DigestPanel() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['reports', 'digest'], queryFn: () => api<DigestPref>('/reports/digest') });
  const save = useMutation({
    mutationFn: (b: { enabled: boolean; hour: number }) =>
      api<DigestPref>('/reports/digest', { method: 'PUT', body: b }),
    onSuccess: (d) => {
      qc.setQueryData(['reports', 'digest'], d);
      toast(d.enabled ? 'Сводка включена' : 'Сводка выключена');
    },
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const d = q.data;
  return (
    <Panel title="Сводка руководителю" meta="приходит каждое утро по времени портала">
      <p className="mb-3 text-[14px] text-ink-2">
        Каждое утро портал собирает сводку: проекты, сроки, деньги по вашему уровню доступа, что ждёт вас. Она
        появляется среди выпусков и приходит выбранными в профиле способами. В почту и Telegram суммы не
        отправляются.
      </p>
      <Checkbox
        className="min-h-11 items-center"
        label="Присылать сводку"
        checked={d.enabled}
        onChange={(e) => save.mutate({ enabled: e.target.checked, hour: d.hour })}
      />
      <Field label="Час отправки" className="mt-3 max-w-48">
        <Select
          className={ctl}
          value={d.hour}
          onChange={(e) => save.mutate({ enabled: d.enabled, hour: Number(e.target.value) })}
        >
          {HOURS.map((h) => (
            <option key={h} value={h}>
              {String(h).padStart(2, '0')}:00
            </option>
          ))}
        </Select>
      </Field>
      {d.lastRunOn ? (
        <p className="mt-3 text-[13px] text-ink-2">Последняя сводка: {date(d.lastRunOn)}</p>
      ) : null}
      {save.error ? <ErrorBox error={save.error} className="mt-3" /> : null}
    </Panel>
  );
}

export function ReportsPage() {
  const search = useSearch({ strict: false }) as { tab?: Tab };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tab: Tab = search.tab ?? 'ready';
  useCrumbs([]);
  return (
    <Page>
      <PageHeader
        title="Отчёты"
        subtitle="Готовые отчёты заказчику, руководителю и на совещание; свои отчёты из конструктора; сводка в 08:00."
      />
      <Tabs<Tab>
        value={tab}
        onChange={(t) => navigate({ to: '/reports', search: { tab: t } as never })}
        items={[
          { value: 'ready', label: 'Готовые отчёты' },
          { value: 'mine', label: 'Мои отчёты' },
          { value: 'digest', label: 'Сводка в 08:00' },
        ]}
      />
      {tab === 'ready' ? (
        <div className="flex flex-col gap-6">
          <LaunchForm
            onDone={(r) => {
              toast(`Выпущен ${r.number}`);
              void qc.invalidateQueries({ queryKey: ['reports', 'runs'] });
              void navigate({ to: '/reports/runs/$id', params: { id: r.id } });
            }}
          />
          <RunsList />
        </div>
      ) : tab === 'mine' ? (
        <MyReports />
      ) : (
        <DigestPanel />
      )}
      <p className="mt-6 flex items-center gap-2 text-[12px] text-ink-2">
        <FileText className="size-3.5" /> Любой выпуск скачивается в Word и PDF.
      </p>
    </Page>
  );
}
